import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import {
  getDutyWorkLocationDisplayCode,
  getDutyAircraftInfo,
  getFlightSegmentSx,
  getCityCodeSegmentSx,
  getRosterFlightChipOutlineColor
} from '../utils/crewDutyRosterUtils';

/**
 * Roster grid / table chip: registration number (when known) + optional city code.
 * No flight/model name — registration only. Plan rows without aircraft still show "Flight".
 * Primary segment: gold if VIP / IR / HILL / CP / TRI / TRE / CHPL / ATPL / Dual Type cleared, else light blue.
 * City code segment: deterministic pastel per city (same city → same colour).
 */
const RosterFlightDutyChip = ({ entry, highlightCleared, isVipCleared, sx = {} }) => {
  const cleared = highlightCleared ?? isVipCleared;
  if (entry?.dutyType === 'SCHEDULED_DUTY_OFF') {
    if (entry.autoGenerated) {
      const until = entry.dutyEndTime
        ? new Date(entry.dutyEndTime).toLocaleString()
        : '';
      const reason =
        entry.restReason === 'WEEKLY_48H'
          ? 'Weekly rest'
          : entry.restReason === 'PART_III_COMP'
            ? 'Comp rest'
            : 'Mandatory rest';
      return (
        <Chip
          size="small"
          label={until ? `${reason} until ${until}` : reason}
          color="warning"
          sx={{
            maxWidth: 220,
            height: 22,
            fontSize: '0.65rem',
            ...sx
          }}
        />
      );
    }
    return (
      <Chip
        size="small"
        label="Off"
        sx={{
          maxWidth: 92,
          height: 22,
          fontSize: '0.7rem',
          bgcolor: '#f3e5f5',
          border: '1px solid #9c27b0',
          ...sx
        }}
      />
    );
  }

  const code = getDutyWorkLocationDisplayCode(entry?.dutyWorkLocationCity, entry?.dutyWorkLocationCode);
  const { registration } = getDutyAircraftInfo(entry);
  const flightSx = getFlightSegmentSx(cleared);
  const codeSx = code ? getCityCodeSegmentSx(entry?.dutyWorkLocationCity, entry?.dutyWorkLocationCode) : {};
  const outline = getRosterFlightChipOutlineColor(cleared);
  const primaryLabel = registration || 'Flight';
  const tooltipParts = [registration, code ? `Loc ${code}` : '', entry?.linkedToFlight ? 'Ops assign' : '']
    .filter(Boolean);
  const tooltip = tooltipParts.join(' · ') || 'Flight';

  return (
    <Tooltip title={tooltip} arrow>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'stretch',
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: outline,
          height: 22,
          fontSize: '0.7rem',
          fontWeight: 600,
          maxWidth: registration ? 200 : 130,
          ...sx
        }}
      >
        <Box
          component="span"
          sx={{
            px: 0.5,
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: registration ? 700 : 600,
            ...flightSx
          }}
          title={primaryLabel}
        >
          {primaryLabel}
        </Box>
        {code ? (
          <Box
            component="span"
            sx={{
              px: 0.5,
              display: 'flex',
              alignItems: 'center',
              borderLeft: '1px solid rgba(0,0,0,0.12)',
              fontWeight: 700,
              ...codeSx
            }}
          >
            {code}
          </Box>
        ) : null}
      </Box>
    </Tooltip>
  );
};

export default RosterFlightDutyChip;
